import { config } from '@config';
import { zip } from 'rxjs';
import { concatMap, tap, map } from 'rxjs/operators';
import { RxUtils, TypeUtils } from '@utils';
import { getStore, RootState } from '@store';
import { httpService } from '@services/http';
import { logger } from '@services/logger';
import { userService } from '@services/user';
import { userInvitationService, UserInvitationState } from '@services/user-invitation';
import { Role } from './role.class';
import { RoleWithAssignees } from './role-with-assignees.class';

export class RoleService {
	private store = getStore().store;

	private getUrl = () => `${config.apiUrl}/role`;

	getAll(orgId?: string) {
		const organizationId = orgId || (this.store.getState() as RootState).Common.loggedinUser?.organization?.id;
		return httpService.get<Role[]>(this.getUrl(), `all/${organizationId}`).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getAll.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(Role, result))
		);
	}

	getAllWithAssignees() {
		return zip(this.getAll(), userInvitationService.getAll(UserInvitationState.PENDING), userService.getAll(true)).pipe(
			map(([roles, invitations, users]) =>
				roles.map(
					(role) =>
						new RoleWithAssignees({
							...role,
							invitedUsers: invitations.filter((user) => user.role.id === role.id),
							users: users.filter((user) => user?.role?.id === role.id)
						})
				)
			)
		);
	}

	create(role: Partial<Role>) {
		const organizationId = (this.store.getState() as RootState).Common.loggedinUser?.organization?.id;
		return httpService.post<Role>(this.getUrl(), '', { body: { ...role, organizationId }, errorHandler: 'notification' }).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.create.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(Role, result))
		);
	}

	update(role: Partial<Role>) {
		return httpService.patch<unknown>(this.getUrl(), `${role.id}`, { body: role, errorHandler: 'notification' }).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.update.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty')))
		);
	}

	delete({ id }: Partial<Role>) {
		return httpService.delete<unknown>(this.getUrl(), `${id}`, { errorHandler: 'notification' }).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.delete.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty')))
		);
	}
}

export const roleService = new RoleService();
